import React, { PropsWithChildren, useState, useEffect } from 'react'
import clsx from 'clsx'
import uniqueId from 'lodash.uniqueid'
import { AnchorNav as AnchorNavStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import { CommonProps } from '../helperTypes'

const useStyles = createUseStyles(AnchorNavStyles, 'anchornav')

type Props = PropsWithChildren<{
  /**
   * By default selected item is updated automatically when window.location.hash changes.<br />
   * set this to true to manage selected item manually.
   * @default false
   **/
  disableAutoUpdate?: boolean
  /** title of anchor nav */
  title?: string
}>

export type AnchorNavProps = Props & CommonProps

type AnchorNavContextType = {
  selectedHash?: string
}

export const AnchorNavContext = React.createContext<AnchorNavContextType>({})

// Check if window is defined (so if in the browser or in node.js).
const isBrowser = typeof window !== 'undefined'

const AnchorNav = (props: AnchorNavProps) => {
  const { children, disableAutoUpdate = false, id: propsId, title, className, ...rest } = props
  const classes = useStyles()
  const defaultId = React.useRef<string>(uniqueId('anchornav-labelid-'))
  const id = propsId || defaultId.current

  // update context when window.location.hash changes
  let [selectedHash, setSelectedHash] = useState(disableAutoUpdate || !isBrowser ? '' : window.location.hash)
  const updateHash = () => setSelectedHash(isBrowser ? window.location.hash : '')
  useEffect(() => {
    if (disableAutoUpdate || !isBrowser) return
    window.addEventListener('hashchange', updateHash)
    return () => {
      window.removeEventListener('hashchange', updateHash)
    }
  }, [disableAutoUpdate])

  return (
    <AnchorNavContext.Provider value={{ selectedHash }}>
      <aside className={clsx(classes.root, className)}>
        <div id={id} className={clsx(classes.title)}>
          {title}
        </div>
        <nav aria-labelledby={id} {...rest}>
          {children}
        </nav>
      </aside>
    </AnchorNavContext.Provider>
  )
}

export default AnchorNav
